import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, X, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PhotoIdentite } from './PhotoIdentite';

// Année d'entrée en formation + suivante (ex. 03/2026 -> 2026-2027), sinon année scolaire en cours
const anneeCarte = (dateEntree) => {
  const match = String(dateEntree || '').match(/20\d{2}/);
  const now = new Date();
  const y = match ? Number(match[0]) : (now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1);
  return `${y}-${y + 1}`;
};

const TYPE_LABELS = {
  'stagiaire_fp': 'Stagiaire',
  'apprenti': 'Apprenti(e)',
  'contrat_pro': 'Contrat Pro',
  'salarie': 'Salarié(e)',
  '': 'Stagiaire',
};

const CarteEtudiantModal = ({ open, onClose, stagiaire, centreProfile, user }) => {
  const [downloading, setDownloading] = useState(false);

  if (!stagiaire && !user) return null;

  // Support both admin view (stagiaire.user_info) and stagiaire's own view (user prop)
  const nom = user?.nom || stagiaire?.user_info?.nom || '';
  const prenom = user?.prenom || stagiaire?.user_info?.prenom || '';
  const centre = stagiaire?.centre_formation || 'Atlantique';
  const rawType = stagiaire?.type_contrat || stagiaire?.statut || '';
  const typeContrat = TYPE_LABELS[rawType] || TYPE_LABELS[''];
  const userId = user?.user_id || stagiaire?.user_id;
  const numeroUai = centreProfile?.numero_uai || '';
  const annee = anneeCarte(stagiaire?.date_entree_formation || stagiaire?.user_info?.date_entree_formation || user?.date_entree_formation);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `/stagiaires/${userId}/carte-etudiant/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Carte_Etudiant_${nom}_${prenom}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Carte etudiant telechargee !');
    } catch (error) {
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-transparent border-none shadow-none">
        <DialogHeader className="sr-only">
          <DialogTitle>Carte Etudiant</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 p-4">
          {/* Card */}
          <div 
            data-testid="carte-etudiant-visual"
            className="relative w-full max-w-[400px] aspect-[1.586/1] rounded-2xl overflow-hidden shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 40%, #0f2035 100%)',
            }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-20"
              style={{ background: 'linear-gradient(135deg, #8DC63F, #6ba32e)' }} />
            <div className="absolute bottom-0 left-0 w-24 h-24 rounded-tr-full opacity-10"
              style={{ background: 'linear-gradient(135deg, #8DC63F, #6ba32e)' }} />
            
            {/* Green accent line */}
            <div className="absolute top-0 left-0 right-0 h-1.5" 
              style={{ background: 'linear-gradient(90deg, #8DC63F, #b5e06a, #8DC63F)' }} />

            <div className="relative h-full flex flex-col justify-between p-4">
              {/* Top row: Logo + Title */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-[10px] tracking-[0.3em] text-slate-400 uppercase font-medium">
                    Carte Etudiant
                  </p>
                  <p className="text-[9px] text-slate-500 mt-0.5" data-testid="carte-etudiant-annee">
                    {annee}
                  </p>
                </div>
                <div className="flex flex-col items-end">
                  <img 
                    src="/logo-fisl-transparent.png" 
                    alt="FISL" 
                    className="h-16 w-auto object-contain drop-shadow-lg"
                    onError={(e) => {
                      e.target.src = '/logo-fisl.png';
                      e.target.onerror = null;
                    }}
                  />
                </div>
              </div>

              {/* Middle: Photo + Identity */}
              <div className="flex items-center gap-3 -mt-1">
                <PhotoIdentite
                  userId={userId}
                  alt={`${prenom} ${nom}`}
                  className="w-14 h-[72px] rounded-lg object-cover border-2 border-[#8DC63F]/40 shadow-md flex-shrink-0"
                  testId="carte-etudiant-photo"
                  fallback={
                    <div className="w-14 h-[72px] rounded-lg bg-slate-700 flex items-center justify-center border-2 border-[#8DC63F]/40 flex-shrink-0">
                      <span className="text-lg font-bold text-[#8DC63F]">
                        {prenom?.[0]}{nom?.[0]}
                      </span>
                    </div>
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="text-white font-bold text-base leading-tight truncate">
                    {prenom} {nom.toUpperCase()}
                  </p>
                  <p className="text-[#8DC63F] text-[10px] font-semibold mt-0.5 leading-tight">
                    BPJEPS Activites du Motonautisme
                  </p>
                  <p className="text-[#8DC63F]/70 text-[9px] leading-tight">
                    et Disciplines Associees
                  </p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="px-1.5 py-0.5 bg-[#8DC63F]/20 text-[#8DC63F] text-[8px] font-bold rounded uppercase tracking-wider">
                      {typeContrat}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bottom: Centre + UAI + CFA */}
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[9px] text-slate-400">
                    Centre {centre}
                  </p>
                  {numeroUai && (
                    <p className="text-[8px] text-slate-500">
                      UAI : {numeroUai}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-medium">
                    CFA FISL
                  </p>
                  <p className="text-[8px] text-slate-500">
                    Formation Internationale
                  </p>
                  <p className="text-[8px] text-slate-500">
                    aux Sports de Loisirs
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-[#8DC63F] hover:bg-[#7ab62e] text-white"
              data-testid="carte-etudiant-download-pdf"
            >
              <Download className="h-4 w-4 mr-2" />
              {downloading ? 'Generation...' : 'Telecharger PDF'}
            </Button>
            <Button variant="outline" onClick={() => onClose(false)} className="bg-white text-slate-800 border-white hover:bg-slate-100" data-testid="carte-etudiant-close">
              <X className="h-4 w-4 mr-2" />
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CarteEtudiantModal;
